// Import the global type declaration
import "@/types/window.d.ts";
import { SegmentAnalyticsAdapter } from "./adapters/segment";
import { RfAnalyticsAdapter } from "./adapters/rf";
import { AnalyticsMethod, AnalyticsIdentity, AppSource, AnalyticsEventData } from "./types";

import { v4 as uuidv4 } from "uuid";
import { AnalyticsAbstractAdapter } from "./adapters/abstract";

export type EventTrackerTrackResult = Promise<
    | { results: PromiseSettledResult<unknown>[]; errors: never[] }
    | { results: never[]; errors: unknown[] }
>;

export type EventTrackerTrack = ({
    method,
    id,
    data,
    identity
}: {
    method: AnalyticsMethod;
    id?: string;
    data?: Record<string, any>;
    identity?: AnalyticsIdentity;
}) => EventTrackerTrackResult;

class EventTracker {
    /**
     * The list of registered analytics adapters.
     */
    private adapters: AnalyticsAbstractAdapter[] = [
        SegmentAnalyticsAdapter.getInstance(),
        RfAnalyticsAdapter.getInstance()
    ];

    /**
     * Trigger an event on all registered analytics adapters.
     */
    /**
     * Run an analytics call, but skip it if we're in local environment.
     * @param method - The adapter method to call
     * @param id - The event id
     * @param data - The data to pass to the method
     * @returns The results of the registered analytics adapters
     */

    trackEvent: EventTrackerTrack = async ({ method, id, data, identity }) => {
        try {
            let eventData: AnalyticsEventData = {
                ...(data || {}),
                _anonymousId: window?.analytics?.user?.().anonymousId?.() || uuidv4(),
                appSource: AppSource.RAPID
            };

            if (identity) {
                const {
                    id,
                    email,
                    name,
                    displayName,
                    workspaceId,
                    workspaceUrl,
                    workspaceName,
                    workspacePlan,
                    workflowId,
                    workflowUrl,
                    workflowName
                } = identity;
                eventData = {
                    ...eventData,
                    id,
                    email,
                    name,
                    displayName,
                    workspaceId,
                    workspaceUrl,
                    workspaceName,
                    workspacePlan,
                    workflowId,
                    workflowUrl,
                    workflowName
                };
            }

            const eventPayload = { method, id, data: eventData };

            if (!eventData.id) eventData.id = eventData._anonymousId;

            const results = await Promise.allSettled(
                this.adapters.map((adapter) => adapter.onEvent(eventPayload))
            );

            results
                .filter((r) => r.status === "rejected")
                .forEach((r) => {
                    console.error(`[Analytics] ${method} error:`, r.reason, id, data);
                });

            return { results, errors: [] };
        } catch (err) {
            console.error(`[Analytics] ${method} error:`, err, id, data);
            return { results: [], errors: [err] };
        }
    };
}

// singleton instance
const eventTracker = new EventTracker();

export default eventTracker;
